import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import { AssessmentData, ContentTypeForComparison, SMSAutoResponse, CommunicationType } from 'domains/content/types';
import { DEFAULT_LANGUAGE_CODE } from 'domains/core/components/LanguageTabBar';
import useCreateSms from 'hooks/mutations/useCreateSms';
import useUpdateSms from 'hooks/mutations/useUpdateSms';
import { URLPaths } from 'models/enums';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText/ContentStatusText';
import { getContentCreatePath } from 'pages/ContentPage/ContentTable/utils';

export enum SmsSaveMode {
    'SAVE' = 'save',
    'EXIT' = 'exit',
}

export type SmsSaveData = {
    name: string;
    content: string;
    isNameTooShort: boolean;
    isUpdate: boolean;
    saveMode: SmsSaveMode;
    status?: ContentStatus;
    assessmentData?: AssessmentData;
    autoResponses?: SMSAutoResponse[];
    contentMultilingual?: Record<string, string>;
    autoResponsesMultilingual?: Record<string, SMSAutoResponse[]>;
};

type SmsPayload = AssessmentData & {
    applicationsAccess: string;
    name: string;
    communicationType: CommunicationType;
    content: string;
    parentFolderId?: number;
    _saveMode?: SmsSaveMode;
    status?: ContentStatus;
    contentMultilingual?: Record<string, string>;
    autoResponsesMultilingual?: Record<string, SMSAutoResponse[]>;
};

type UseSmsSubmissionProps = {
    applicationsAccess: string[];
    communicationType: CommunicationType;
    id: string | null;
    parentFolderId: number;
    isCreate: boolean;
    isCopy: boolean;
    setErrorMessage: (message: string) => void;
};

const useSmsSubmission = ({
    applicationsAccess,
    communicationType,
    id,
    parentFolderId,
    isCreate,
    isCopy,
    setErrorMessage,
}: UseSmsSubmissionProps) => {
    const history = useHistory();
    const queryClient = useQueryClient();

    // Success callback for API operations
    const onSuccess = useCallback(
        (data: any, variables: SmsPayload) => {
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'sms',
            });

            if (isCreate || isCopy) {
                history.replace(
                    getContentCreatePath({
                        contentType: ContentTypeForComparison.SMS,
                        id: data.id,
                        parentFolderId,
                    })
                );
            }

            if (variables._saveMode === 'exit') {
                const contentPath = parentFolderId ? `${URLPaths.CONTENT_FOLDER}/${parentFolderId}` : URLPaths.CONTENT;
                history.push(contentPath);
            }
        },
        [queryClient, isCreate, isCopy, history, parentFolderId]
    );

    // API mutations
    const { mutate: createSms, isLoading: isCreateLoading, error: createError } = useCreateSms({
        onSuccess,
        onError: (error) => {
            setErrorMessage(error.message || 'Failed to save SMS content');
        },
    });

    const { mutate: updateSms, isLoading: isUpdateLoading, error: updateError } = useUpdateSms(id, {
        onSuccess,
        onError: (error) => {
            setErrorMessage(error.message || 'Failed to update SMS content');
        },
    });

    // Save handlers
    const handleSave = useCallback(
        ({
            name,
            content,
            isNameTooShort,
            isUpdate,
            saveMode = SmsSaveMode.SAVE,
            status,
            assessmentData,
            autoResponses,
            contentMultilingual,
            autoResponsesMultilingual,
        }: SmsSaveData) => {
            if (!name || isNameTooShort) {
                if (saveMode === 'exit') {
                    setErrorMessage('SMS name must be at least 3 characters');
                }
                return;
            }

            // When multilingual is active, the primary columns must always carry en-US content
            // so that consumers without the ML flag (backend workers, legacy API callers) still
            // see the correct default language content.
            const primaryContent = contentMultilingual?.[DEFAULT_LANGUAGE_CODE] ?? content;
            const primaryAutoResponses = autoResponsesMultilingual?.[DEFAULT_LANGUAGE_CODE] ?? autoResponses;

            const getAutoResponsesToSave = (responses: SMSAutoResponse[] | undefined) => {
                if (!responses?.length) return undefined;
                return responses.filter((autoResponse) => !!autoResponse.keywords.length && !!autoResponse.text.length);
            };
            const autoResponsesToSave = getAutoResponsesToSave(primaryAutoResponses);

            const smsData = {
                name,
                content: primaryContent,
                _saveMode: saveMode,
                ...assessmentData,
                ...(!!autoResponsesToSave?.length && { autoResponses: autoResponsesToSave }),
                communicationType: communicationType,
                applicationsAccess: applicationsAccess.join(','),
                ...(status && { status }),
                ...(contentMultilingual && { contentMultilingual }),
                ...(autoResponsesMultilingual && { autoResponsesMultilingual }),
            };

            if (isUpdate) {
                updateSms(smsData);
            } else {
                const createData = {
                    ...smsData,
                    ...(parentFolderId ? { parentFolderId } : {}),
                };
                createSms(createData);
            }
        },
        [createSms, updateSms, parentFolderId, setErrorMessage, applicationsAccess, communicationType]
    );

    return {
        createSms,
        updateSms,
        isCreateLoading,
        isUpdateLoading,
        createError,
        updateError,
        handleSave,
    };
};

export default useSmsSubmission;
